import React from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';

import useUserContext from '../../hooks/use-user-context';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { fontWeights } from '../../theme/type';

import Dropdown from '../dropdown/Dropdown';
import DropdownMenu from '../dropdown/DropdownMenu';
import DropdownPanel from '../dropdown/DropdownPanel';
import DropdownFocusWrapper from '../dropdown/DropdownFocusWrapper';
import Link from '../Link';
import TextRenderer from '../TextRenderer';

const Container = styled.nav`
  padding-left: ${rem(20)};
`;

const Menu = styled.ul`
  display: flex;
`;

const MenuItem = styled.li`
  display: inline-block;
  font-size: ${rem(14)};
`;

const MenuLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0 ${rem(8)};
  color: ${colors.blue['800']};
  height: ${rem(90)};
  font-weight: ${fontWeights.semiBold};
  transition: color .2s ease;

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const NavbarMainUtilityMenu = ({ type, setIsDropdownOpen }) => {
  const data = useStaticQuery(graphql`
    query NavbarMainUtilityMenu {
      allContentfulNavigation(filter: {location: {eq: "main-utility"}}) {
        nodes {
          title
          ...NavigationFragment
        }
      }
    }
  `);
  const { language } = useUserContext();

  // Choose the utility menu based on type and language
  let utilityMenuTitle = 'Main Utility Menu';
  if (type !== 'Normal') utilityMenuTitle = `${type} ${utilityMenuTitle}`;
  const contentfulNavigation = data.allContentfulNavigation.nodes.find(
    (navigation) => navigation.language === language
      && navigation.title === utilityMenuTitle
  ) || { navigationItems: [] };
  const { navigationItems } = contentfulNavigation;

  return (
    <Container aria-label="Utility Menu">
      <Menu>
        {navigationItems.map((navigationItem) => (
          <MenuItem key={navigationItem.id}>
            <Dropdown>
              <DropdownFocusWrapper
                hasDropdown={
                  navigationItem.childNavigationItems
                    && navigationItem.childNavigationItems.length > 0
                }
                setIsDropdownOpen={setIsDropdownOpen}
              >
                <MenuLink
                  to={navigationItem.url}
                  targetBlank={navigationItem.targetBlank}
                >
                  <TextRenderer text={navigationItem.title} />
                </MenuLink>
                {navigationItem.childNavigationItems
                  && navigationItem.childNavigationItems.length > 0 && (
                    <DropdownPanel
                      label={navigationItem.title}
                    >
                      <DropdownMenu items={navigationItem.childNavigationItems} />
                    </DropdownPanel>
                )}
              </DropdownFocusWrapper>
            </Dropdown>
          </MenuItem>
        ))}
      </Menu>
    </Container>
  );
};

NavbarMainUtilityMenu.propTypes = {
  type: PropTypes.string,
};

export default NavbarMainUtilityMenu;
